import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IItemCreationSubsectionInputProps } from '~/Components';
import { Repeat } from '~/Components/Icons';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { ExtendedEventSeries } from '../../../extendedEventSeries.model';
import { useWeeklyRecurrence } from './useWeeklyRecurrence';
import { useMonthlyRecurrence } from './useMonthlyRecurrence';
import { AccessList, useHasAccess } from '~/Hooks';
import { EventScheduleTimeSeriesFrequencyNone } from '../EventScheduleTimeSeriesFrequencyNone';
import { TIME_FREQUENCY } from '~/Models/DTO/EventSeries.model';

export const useTimeSeriesRecurrence = ({
  timeSeries,
  hasFutureBookings,
}: {
  timeSeries: ExtendedEventSeries;
  hasFutureBookings?: boolean;
}) => {
  const { t } = useTranslation();
  const { hasAccess } = useHasAccess();

  const weeklyRecurrence = useWeeklyRecurrence({ timeSeries, hasFutureBookings });
  const monthlyRecurrence = useMonthlyRecurrence({ timeSeries, hasFutureBookings });

  const timeSeriesRecurrenceChildren = useMemo(() => {
    if (timeSeries.recurrence === TIME_FREQUENCY.MONTHLY) {
      return monthlyRecurrence;
    } else if (timeSeries.recurrence === TIME_FREQUENCY.WEEKLY) {
      return weeklyRecurrence;
    } else {
      return [
        {
          customElement: <EventScheduleTimeSeriesFrequencyNone startDate={timeSeries.startDate} />,
        },
      ];
    }
  }, [timeSeries, weeklyRecurrence, monthlyRecurrence]);

  const timeSeriesRecurrence: IItemCreationSubsectionInputProps<Partial<ExtendedEventSeries>>[] = [
    {
      label: t('time.recurrence'),
      type: ItemCreationValidInput.SELECT,
      name: 'recurrence',
      icon: <Repeat />,
      disabled: hasFutureBookings && !hasAccess(AccessList.EDIT_SCHEDULE_WITH_BOOKINGS),
      options: [
        ...(hasAccess(AccessList.SCHEDULE_RECURRENCE_MONTHLY)
          ? [{ label: t('time.frequency.Monthly'), value: TIME_FREQUENCY.MONTHLY }]
          : []),
        { label: t('time.frequency.Weekly'), value: TIME_FREQUENCY.WEEKLY },
        { label: t('app.none'), value: TIME_FREQUENCY.NONE },
      ],
      children: timeSeriesRecurrenceChildren,
    },
  ];

  return { timeSeriesRecurrence };
};
